/**
 * loadFBX — the Load stage for FBX (ADR-008 §2). Format knowledge ends here:
 * downstream conversion consumes the loaded scene graph only, exactly as for
 * GLB — no per-format branches past this file, ever.
 * FBX specifics owned here: takes are ENUMERATED AND CLASSIFIED, never
 * auto-selected (a Mixamo file ships its walk beside a vestigial empty
 * "Take 001"; v2-era pose takes carry static tracks) — selection is the
 * caller's decision. Conventions come from the file's GlobalSettings via the
 * loader (units) and the loader's up-axis normalization — declared with
 * provenance, never sniffed.
 * Depends on: three (FBXLoader addon). Depended on by: app/import, fixtures.
 */
import { AnimationClip, Object3D } from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { FbxUnitDisagreementError, UnsupportedFbxAxesError, ZUpFbxError } from './errors.js';
import type { ConventionDeclaration } from './import-report.js';
import { recordFilePaint } from './zones/file-paint.js';

/**
 * What a take's tracks actually contain — measured, not name-guessed.
 * 'static-pose' is first-class: a pose take is a measured rest-pose reference
 * (future reorientation evidence), typed distinctly from playable animation.
 */
export type TakeClassification = 'animation' | 'static-pose' | 'empty';

export interface FbxTake {
  readonly name: string;
  readonly clip: AnimationClip;
  readonly classification: TakeClassification;
}

export interface LoadedFbx {
  readonly scene: Object3D;
  /** ALL takes in file order, classified. The caller selects — never clips[0]. */
  readonly takes: readonly FbxTake[];
  readonly convention: ConventionDeclaration;
  /** Authored (file) node name lookup — FBXLoader strips reserved characters
   *  (e.g. 'mixamorig:Hips' → 'mixamorigHips') but keeps the original in
   *  userData (ADR-008 §2j). */
  readonly authoredNameFor: (object: Object3D) => string | undefined;
  /** FBXLoader does not surface the FBX Creator field — honestly unknown. */
  readonly generator: string | null;
  /** FBX has no extension mechanism — honestly empty, not unchecked. */
  readonly unhandledExtensions: readonly string[];
}

/**
 * Classify one take by measuring its tracks. Constancy uses EXACT equality:
 * a jittery near-static take classifies as 'animation' — the safe direction
 * (a mislabeled pose plays as a dull clip; a mislabeled animation would hide
 * real motion behind a pose label).
 */
export function classifyTake(clip: AnimationClip): TakeClassification {
  let totalKeys = 0;
  for (const track of clip.tracks) totalKeys += track.times.length;
  if (totalKeys === 0) return 'empty';
  for (const track of clip.tracks) {
    const stride = track.getValueSize();
    for (let k = 1; k < track.times.length; k += 1) {
      for (let i = 0; i < stride; i += 1) {
        if (track.values[k * stride + i] !== track.values[i]) return 'animation';
      }
    }
  }
  return 'static-pose';
}

const BINARY_MAGIC = 'Kaydara FBX Binary';

/** The declared FBX axis system + units, read from the GlobalSettings NODE. */
export interface FbxGlobalSettings {
  readonly unitScaleFactor?: number;
  readonly upAxis?: number;
  readonly upAxisSign?: number;
  readonly frontAxis?: number;
  readonly frontAxisSign?: number;
  readonly coordAxis?: number;
  readonly coordAxisSign?: number;
}

const GLOBAL_SETTINGS_PROPS = [
  ['unitScaleFactor', 'UnitScaleFactor'],
  ['upAxis', 'UpAxis'],
  ['upAxisSign', 'UpAxisSign'],
  ['frontAxis', 'FrontAxis'],
  ['frontAxisSign', 'FrontAxisSign'],
  ['coordAxis', 'CoordAxis'],
  ['coordAxisSign', 'CoordAxisSign'],
] as const;

/** Walk the typed property records after a matched name; first scalar wins. */
function readScalarAfter(u8: Uint8Array, view: DataView, offset: number, end: number): number | undefined {
  let o = offset;
  for (let guard = 0; guard < 8 && o < end; guard += 1) {
    const type = u8[o] as number;
    o += 1;
    if (type === 0x53 /* S */ || type === 0x52 /* R */) {
      o += 4 + view.getUint32(o, true);
    } else if (type === 0x44 /* D */) {
      return view.getFloat64(o, true);
    } else if (type === 0x46 /* F */) {
      return view.getFloat32(o, true);
    } else if (type === 0x49 /* I */) {
      return view.getInt32(o, true);
    } else if (type === 0x4c /* L */) {
      return Number(view.getBigInt64(o, true));
    } else {
      return undefined; // not a scalar property record — refuse to guess
    }
  }
  return undefined;
}

/** Find 'S'+len+name inside [start,end) and parse its scalar value. */
function readBinaryProp(
  u8: Uint8Array,
  view: DataView,
  name: string,
  start: number,
  end: number,
): number | undefined {
  outer: for (let i = Math.max(start, 5); i <= end - name.length; i += 1) {
    if (u8[i - 5] !== 0x53 || view.getUint32(i - 4, true) !== name.length) continue;
    for (let j = 0; j < name.length; j += 1) {
      if (u8[i + j] !== name.charCodeAt(j)) continue outer;
    }
    return readScalarAfter(u8, view, i + name.length, end);
  }
  return undefined;
}

/**
 * Read the GlobalSettings NODE from the FILE BYTES — the declaration source
 * itself, independent of loader plumbing. FBXLoader stamps unitScaleFactor
 * on a wrapper group its root-unwrap path may discard (Codex, PR #20), and
 * ignores the front/coord axes entirely (review, PR #20).
 *
 * Binary: the top-level node records are WALKED structurally (endOffset
 * chaining, 32/64-bit per file version) to locate the node named
 * "GlobalSettings"; property reads are scoped to its byte range, so a
 * same-named record elsewhere (e.g. a Definitions template) can never be
 * mistaken for the declaration (review P2, PR #20). ASCII: the block from
 * the top-level "GlobalSettings:" line to the next top-level section.
 */
export function readFbxGlobalSettings(bytes: ArrayBuffer): FbxGlobalSettings | undefined {
  const view = new DataView(bytes);
  const u8 = new Uint8Array(bytes);
  const isBinary =
    u8.length > BINARY_MAGIC.length + 6 &&
    BINARY_MAGIC.split('').every((ch, i) => u8[i] === ch.charCodeAt(0));

  if (!isBinary) {
    const text = new TextDecoder('latin1').decode(u8);
    const blockStart = /^GlobalSettings:/m.exec(text);
    if (!blockStart) return undefined;
    const rest = text.slice(blockStart.index + blockStart[0].length);
    const nextSection = /^\w+:/m.exec(rest);
    const block = nextSection ? rest.slice(0, nextSection.index) : rest;
    const out: Record<string, number> = {};
    for (const [key, name] of GLOBAL_SETTINGS_PROPS) {
      const match = new RegExp(`P:\\s*"${name}"\\s*(?:,\\s*"[^"]*"){3}\\s*,\\s*([-+0-9.eE]+)`).exec(block);
      if (match) out[key] = Number(match[1]);
    }
    return out;
  }

  // Binary: header is magic(21) + 0x1A 0x00 + version u32; node record
  // headers are 3×u32 before version 7500, 3×u64 from 7500 on.
  const version = view.getUint32(23, true);
  const big = version >= 7500;
  let o = 27;
  while (o < u8.length) {
    const end = big ? Number(view.getBigUint64(o, true)) : view.getUint32(o, true);
    if (end === 0 || end <= o) break; // null record terminates the sibling list
    const nameLenOffset = o + (big ? 24 : 12);
    const nameLen = u8[nameLenOffset] as number;
    const nodeName = new TextDecoder('latin1').decode(
      u8.subarray(nameLenOffset + 1, nameLenOffset + 1 + nameLen),
    );
    if (nodeName === 'GlobalSettings') {
      const start = nameLenOffset + 1 + nameLen;
      const out: Record<string, number> = {};
      for (const [key, name] of GLOBAL_SETTINGS_PROPS) {
        const value = readBinaryProp(u8, view, name, start, end);
        if (value !== undefined) out[key] = value;
      }
      return out;
    }
    o = end;
  }
  return undefined;
}

/**
 * The one axis tuple this stage supports: the Y-up/+Z-front/+X-coord system
 * (Maya Y-up — what Mixamo exports, specimen-proven). Each field is checked
 * only when DECLARED: an absent field falls back to the FBX default, which
 * is this tuple; an explicit contradiction refuses loudly.
 */
function assertSupportedAxes(globals: FbxGlobalSettings): void {
  if (globals.upAxis === 2) throw new ZUpFbxError();
  const violations: string[] = [];
  const expect = (field: keyof FbxGlobalSettings, expected: number): void => {
    const declared = globals[field];
    if (declared !== undefined && declared !== expected) {
      violations.push(`${field}=${declared} (supported: ${expected})`);
    }
  };
  expect('upAxis', 1);
  expect('upAxisSign', 1);
  expect('frontAxis', 2);
  expect('frontAxisSign', 1);
  expect('coordAxis', 0);
  expect('coordAxisSign', 1);
  if (violations.length > 0) throw new UnsupportedFbxAxesError(violations);
}

export function loadFBX(bytes: ArrayBuffer, loader: FBXLoader = new FBXLoader()): LoadedFbx {
  const scene = loader.parse(bytes, '');
  const globals = readFbxGlobalSettings(bytes);

  // Axis system: FBXLoader compensates UpAxis only (by rotating the returned
  // root — a frame OUTSIDE the IBM bind space conversion reads, §2b) and
  // ignores Front/Coord entirely, so a declaration this stage cannot map
  // honestly must REFUSE, not default (review P1, PR #20). The rotation
  // check backstops the Z-up case when GlobalSettings itself is unreadable.
  if (globals) assertSupportedAxes(globals);
  if (Math.abs(scene.rotation.x + Math.PI / 2) < 1e-9 && scene.rotation.y === 0 && scene.rotation.z === 0) {
    throw new ZUpFbxError();
  }

  // Units: FBX GlobalSettings.UnitScaleFactor is centimetres-per-unit (the
  // format's reference unit is cm); geometry is never rescaled by the loader.
  // The file bytes are the declaration source (the loader's copy can vanish
  // in its root-unwrap path); when the loader DID surface a value, the two
  // must agree — a disagreement means the byte read went wrong, and a
  // silent wrong unit is the PR #17 bug class. Absent everywhere, the
  // FBX SDK default (1 = cm) is declared under its own provenance — a
  // default is still a declaration, never a measurement.
  const scanned = globals?.unitScaleFactor;
  const fromLoader = (scene.userData as { unitScaleFactor?: number }).unitScaleFactor;
  if (scanned !== undefined && fromLoader !== undefined && scanned !== fromLoader) {
    throw new FbxUnitDisagreementError(scanned, fromLoader);
  }
  const unitScaleFactor = scanned ?? fromLoader;
  const convention: ConventionDeclaration = Object.freeze({
    metersPerUnit: (unitScaleFactor ?? 1) / 100,
    // Honest by construction: any DECLARED axis contradiction was refused
    // above, so the bind data is genuinely Y-up with the +Z front the FBX
    // default asserts. A rig that lies about facing beyond its declaration
    // is corrected downstream by ADR-004 geometric evidence (ADR-008 §2c).
    upAxis: 'Y',
    facing: '+Z',
    provenance: unitScaleFactor !== undefined ? 'file:fbx-globals' : 'spec:fbx-default',
  });

  const authoredNameFor = (object: Object3D): string | undefined => {
    const original = (object.userData as { originalName?: unknown }).originalName;
    return typeof original === 'string' && original.length > 0 ? original : undefined;
  };

  const takes: FbxTake[] = scene.animations.map((clip) => ({
    name: clip.name,
    clip,
    classification: classifyTake(clip),
  }));

  // The skin as the file painted it, before any run re-paints it (zones/file-paint).
  recordFilePaint(scene);
  return {
    scene,
    takes,
    convention,
    authoredNameFor,
    generator: null,
    unhandledExtensions: [],
  };
}
